import { useNavigator } from '@repo/toolkit/use-navigator'
import { SheetOutlineIcon } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getYouthAndSportNumber } from '../../api/youth-and-sport-numbers/get-youth-and-sport-number'
import { updateYouthAndSportNumber } from '../../api/youth-and-sport-numbers/update-youth-and-sport-number'
import { ItemNotFound } from '../../components/elements/item-not-found'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import YouthAndSportNumberForm from '../../components/pages/youth-and-sport-number/youth-and-sport-number-form'
import { Paths } from '../../lib/constants/path'
import { updateButtonsConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import NotFoundPage from '../common/not-found'

const YouthAndSportNumberUpdatePage = () => {
  const { id } = useParams<{ id: string }>()
  const { navigate, back } = useNavigator()
  const queryClient = useQueryClient()

  if (!id || isNaN(+id)) return <ItemNotFound title="Identifiant invalide" />

  const { mutateAsync } = useMutation({
    mutationFn: updateYouthAndSportNumber,
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({ exact: true, queryKey: ['youth-and-sport-numbers', id] })
      toast.success("N° d'enregistrements modifié avec succès")
    }
  })

  const { data, isLoading } = useQuery({
    queryKey: ['youth-and-sport-numbers', id],
    queryFn: () => getYouthAndSportNumber(+id)
  })

  if (isLoading) return <LoadingScreen />
  if (!data) return <NotFoundPage />

  return (
    <div>
      <UserNavBar
        title="Modifier le N° d'enregistrement Jeunesse et Sport"
        icon={SheetOutlineIcon}
      />
      <div className="h-4" />
      <YouthAndSportNumberForm
        defaultValues={{
          number: data.number,
          periodId: data.periodId,
          countryId: data.countryId,
          centerIds: data.centers.map((center) => center.id)
        }}
        buttonsConfig={updateButtonsConfig({
          onCancel: () => back(Paths.YOUTH_AND_SPORT_NUMBER_LIST_PAGE),
          onUpdate: (values) => mutateAsync({ id: data.id, ...values }),
          onUpdateThenCreate: async (values) => {
            await mutateAsync({ id: data.id, ...values })
            navigate(Paths.YOUTH_AND_SPORT_NUMBER_CREATE_PAGE)
          },
          onUpdateThenList: async (values) => {
            await mutateAsync({ id: data.id, ...values })
            navigate(Paths.YOUTH_AND_SPORT_NUMBER_LIST_PAGE, { replace: true })
          }
        })}
      />
    </div>
  )
}

export default YouthAndSportNumberUpdatePage
